import React, { useMemo } from 'react';

import styled from 'styled-components';

import { useAppSelector } from 'store/hook';
import { CommunityContract } from 'types/contracts';

import { useWidgetContext } from './WidgetContext';
import WidgetFiltersInfoLabel from './WidgetFiltersInfoLabel';
import WidgetFiltersInfoSelect from './WidgetFiltersInfoSelect';
import { isCommunityCCRC } from '../../../components/Community/utils';
import { useCommunityViewContext } from '../../../components/View/CommunityViewContext';
import { COMMUNITY_CONTRACT_SELECTION_TYPE } from '../../../constants';
import {
  CARE_TYPE_AGGREGATED_WIDGETS,
  SWOT_CATEGORIES,
  WIDGET_FILTER_TYPES,
  WIDGET_FILTERS,
  WIDGET_TYPES,
} from '../constants';
import { DATA_RANGE_TYPE } from '../Widget/constants';

const Container = styled.div`
  display: flex;
  font-size: 12px;
  color: var(--text-secondary);
  gap: 4px;
  white-space: nowrap;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;

  span:last-child {
    display: none;
  }
`;

const WidgetFiltersInfo = () => {
  const { livingTypes = [], apartmentTypes = [] } = useAppSelector((state) => state.global);
  const { communityId, community, competitors } = useCommunityViewContext();
  const { type, filters, rangeType, setMeta, meta = { reviewsCommunityOverride: undefined } } = useWidgetContext();
  const {
    careTypes,
    roomTypes,
    roomTypesPriceDisplay,
    careLevels,
    medicationLevels,
    reviewSources,
    reviewCategories,
    competitors: filteredCompetitors,
    swotCategories,
    communityContractType,
  } = filters;
  const { reviewsCommunityOverride } = meta;
  const handleReviewsCompetitorSelection = (selectedCommunityId: number) => {
    setMeta({ reviewsCommunityOverride: selectedCommunityId });
  };

  const isCompetitorInFilters = (competitorId: number) => {
    return !filteredCompetitors.length || filteredCompetitors.includes(competitorId);
  };
  const filteredReviewsCommunityOverride = useMemo(
    () => (isCompetitorInFilters(reviewsCommunityOverride) ? reviewsCommunityOverride : undefined),
    [reviewsCommunityOverride, filteredCompetitors]
  );
  const competitorOptions = [
    { id: communityId, name: 'My Community' },
    ...(competitors || [])
      .filter((competitor: any) => isCompetitorInFilters(competitor.id))
      .map((competitor: any) => ({ id: competitor.id, name: competitor.comunity_name })),
  ];

  const averageLabelConditions = [
    WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.CARE_TYPE) &&
      careTypes?.length !== 1 &&
      type !== WIDGET_TYPES.INCENTIVES,
    WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.ROOM_TYPE) && roomTypes?.length !== 1,
    WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.CARE_LEVEL) && careLevels?.length !== 1,
    WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.MEDICATION_LEVEL) && medicationLevels?.length !== 1,
    WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.REVIEW_CATEGORY) && reviewCategories?.length !== 1,
    WIDGET_FILTERS[type]?.includes(WIDGET_FILTER_TYPES.REVIEW_SOURCE) && reviewSources?.length !== 1,
    rangeType === DATA_RANGE_TYPE.OVERTIME,
  ];

  const showAverageLabel =
    averageLabelConditions.some((condition) => condition) &&
    (!CARE_TYPE_AGGREGATED_WIDGETS.includes(type) || careTypes?.length !== 1) &&
    type !== WIDGET_TYPES.PRICING_COMPARISON;
  const averageLablel = CARE_TYPE_AGGREGATED_WIDGETS.includes(type)
    ? careTypes?.length === 0 || careTypes?.length === 5
      ? 'Average for all care types'
      : 'Average of selected room types'
    : 'Average';
  const swotCategoryOptions = SWOT_CATEGORIES.map((category) => ({ id: category.value, name: category.label }));
  const livingTypeOptions = livingTypes.map((type) => ({ id: type.id, name: type.type }));
  const apartmentTypesOptions = apartmentTypes.map((type) => ({ id: type.id, name: type.type }));
  const communityContractTypeOptions = (community?.contracts ?? []).map((communityContract: CommunityContract) => {
    const prefix = filters.communityContractSelectionType === COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE ? '~' : '';
    const refundPercentage = communityContract.refund_percentage
      ? communityContract.refund_percentage.toString().concat('%')
      : '';
    return {
      id: communityContract.id,
      name: `${prefix}${communityContract.name} ${refundPercentage}`,
    };
  });

  return (
    <Container>
      <WidgetFiltersInfoLabel
        widgetType={type}
        filterType={WIDGET_FILTER_TYPES.CARE_TYPE}
        selection={careTypes}
        options={livingTypeOptions}
      />
      <WidgetFiltersInfoLabel
        widgetType={type}
        filterType={WIDGET_FILTER_TYPES.ROOM_TYPE}
        selection={roomTypes}
        options={apartmentTypesOptions}
      />
      <WidgetFiltersInfoLabel widgetType={type} filterType={WIDGET_FILTER_TYPES.CARE_LEVEL} selection={careLevels} />
      <WidgetFiltersInfoLabel
        widgetType={type}
        filterType={WIDGET_FILTER_TYPES.MEDICATION_LEVEL}
        selection={medicationLevels}
      />
      <WidgetFiltersInfoLabel
        widgetType={type}
        filterType={WIDGET_FILTER_TYPES.REVIEW_SOURCE}
        selection={reviewSources}
      />
      <WidgetFiltersInfoLabel
        widgetType={type}
        filterType={WIDGET_FILTER_TYPES.REVIEW_CATEGORY}
        selection={reviewCategories}
      />
      {type === WIDGET_TYPES.REVIEWS && (
        <WidgetFiltersInfoSelect
          selection={filteredReviewsCommunityOverride ?? communityId}
          onSelect={handleReviewsCompetitorSelection}
          options={competitorOptions}
        />
      )}
      <WidgetFiltersInfoLabel
        widgetType={type}
        filterType={WIDGET_FILTER_TYPES.SWOT_CATEGORY}
        selection={swotCategories}
        options={swotCategoryOptions}
      />
      {isCommunityCCRC(community?.rental_types ?? []) && (
        <WidgetFiltersInfoLabel
          widgetType={type}
          filterType={WIDGET_FILTER_TYPES.COMMUNITY_CONTRACT_TYPE}
          selection={[communityContractType || 0]}
          options={communityContractTypeOptions}
        />
      )}
      {showAverageLabel && (
        <>
          <span>{averageLablel}</span>
          <span>·</span>
        </>
      )}
    </Container>
  );
};

export default WidgetFiltersInfo;
